import { v4 as uuid } from "uuid";
import { z } from "zod";
import { calcANZValueAdd } from "../../utils/calc";
export const contentSourceLineItemSchema = z.object({
    abn: z.string(),
    name: z.string(),
    postCode: z.string()
});
export const packageLocalContentLineItemSchema = z.object({
    anzValueAdd: z.number(),
    contractContent: z.number(),
    contractorPackageId: z.string(),
    created: z.date(),
    districtValueAdd: z.number(),
    id: z.string(),
    importValueAdd: z.number(),
    localValueAdd: z.number(),
    manufacturerAbns: z.array(contentSourceLineItemSchema),
    projectId: z.string(),
    projectLocalContentId: z.string(),
    regionalValueAdd: z.number(),
    reportId: z.string(),
    smeCount: z.number(),
    steelMass: z.number(),
    supplierAbns: z.array(contentSourceLineItemSchema),
    supplierCount: z.number(),
    value: z.number()
});
export const recentlyUsedLocalContentABNLineItemSchema = z.object({
    abn: z.string(),
    companyName: z.string(),
    created: z.date(),
    frequency: z.number(),
    id: z.string(),
    postCode: z.string(),
    projectId: z.string()
});
export const createDefaultPackageLocalContent = (projectId, reportId, packageId, projectLocalContentId) => {
    return {
        anzValueAdd: 0,
        contractContent: 0,
        contractorPackageId: packageId,
        created: new Date(),
        districtValueAdd: 0,
        id: uuid(),
        importValueAdd: 0,
        localValueAdd: 0,
        manufacturerAbns: [],
        projectId,
        projectLocalContentId,
        regionalValueAdd: 0,
        reportId,
        smeCount: 0,
        steelMass: 0,
        supplierAbns: [],
        supplierCount: 0,
        value: 0
    };
};
export const aggregateLocalContentItems = (projectLocalContent, items) => {
    const result = {};
    const aggregateMap = new Map();
    //Generate total value for each item
    for (const item of items) {
        const matchingProjectLocalContent = projectLocalContent.find((plc) => plc.id === item.projectLocalContentId);
        if (!matchingProjectLocalContent) {
            throw new Error("No matching project local content found");
        }
        const description = matchingProjectLocalContent.itemDescription;
        if (!aggregateMap.has(description)) {
            aggregateMap.set(description, {
                contractContent: 0,
                localValueAdd: 0,
                localValueAddDollar: 0,
                totalValue: 0
            });
        }
        const aggregate = aggregateMap.get(description);
        aggregateMap.set(description, {
            ...aggregate,
            totalValue: aggregate.totalValue + item.value
        });
    }
    //console.log("Total value for each item:");
    //console.log(aggregateMap);
    //Generate local value dollar for each item
    for (const item of items) {
        const matchingProjectLocalContent = projectLocalContent.find((plc) => plc.id === item.projectLocalContentId);
        if (!matchingProjectLocalContent) {
            throw new Error("No matching project local content found");
        }
        const description = matchingProjectLocalContent.itemDescription;
        const aggregate = aggregateMap.get(description);
        aggregateMap.set(description, {
            ...aggregate,
            localValueAddDollar: aggregate.localValueAddDollar + item.value * (item.localValueAdd / 100)
        });
    }
    //console.log("Local value dollar for each item:");
    //console.log(aggregateMap);
    //Generate local value add for each item
    for (const item of items) {
        const matchingProjectLocalContent = projectLocalContent.find((plc) => plc.id === item.projectLocalContentId);
        if (!matchingProjectLocalContent) {
            throw new Error("No matching project local content found");
        }
        const description = matchingProjectLocalContent.itemDescription;
        const aggregate = aggregateMap.get(description);
        const localValueAdd = aggregate.totalValue > 0
            ? (aggregate.localValueAddDollar / aggregate.totalValue) * 100
            : 0;
        const contractContent = aggregate.totalValue > 0 ? item.value / aggregate.totalValue : 0;
        aggregateMap.set(description, {
            ...aggregate,
            contractContent: aggregate.contractContent + contractContent,
            localValueAdd
        });
    }
    //console.log("Local value add for each item:");
    //console.log(aggregateMap);
    let totalValueForAllItems = 0;
    for (const item of items) {
        totalValueForAllItems += item.value;
    }
    items.forEach((item) => {
        const matchingProjectLocalContent = projectLocalContent.find((plc) => plc.id === item.projectLocalContentId);
        if (!matchingProjectLocalContent) {
            throw new Error("No matching project local content found");
        }
        const description = matchingProjectLocalContent.itemDescription;
        if (!result[description]) {
            result[description] = {
                item: {
                    ...item,
                    anzValueAdd: 0,
                    contractContent: 0,
                    importValueAdd: 0,
                    localValueAdd: 0,
                    smeCount: 0,
                    steelMass: 0,
                    supplierCount: 0,
                    value: 0
                },
                manufacturers: [],
                suppliers: [],
                totalValue: 0
            };
        }
        const aggregate = aggregateMap.get(description);
        const aggregatedItem = result[description].item;
        aggregatedItem.manufacturerAbns.push(...item.manufacturerAbns);
        aggregatedItem.supplierAbns.push(...item.supplierAbns);
        aggregatedItem.value += item.value;
        aggregatedItem.steelMass += item.steelMass;
        aggregatedItem.smeCount =
            (aggregatedItem.smeCount ?? 0) + (item?.smeCount ?? 0);
        aggregatedItem.supplierCount =
            (aggregatedItem.supplierCount ?? 0) + (item?.supplierCount ?? 0);
        aggregatedItem.importValueAdd += item.importValueAdd;
        aggregatedItem.localValueAdd = aggregate.localValueAdd;
        aggregatedItem.contractContent = aggregate.contractContent;
        aggregatedItem.anzValueAdd += calcANZValueAdd(item.localValueAdd, item.contractContent);
    });
    const aggregatedItems = Object.values(result).map((entry) => {
        const { item } = entry;
        item.contractContent = totalValueForAllItems
            ? item.value / totalValueForAllItems
            : 0;
        item.importValueAdd = Math.min(100 - item.localValueAdd, 100);
        // Deduplicate supplierAbns and manufacturerAbns based on abn
        item.supplierAbns = item.supplierAbns.filter((sabn, index, self) => sabn.abn && self.findIndex((s) => s.abn === sabn.abn) === index);
        item.manufacturerAbns = item.manufacturerAbns.filter((mabn, index, self) => mabn.abn && self.findIndex((m) => m.abn === mabn.abn) === index);
        return item;
    });
    return getSortedLocalContent(aggregatedItems);
};
export const getSortedLocalContent = (items) => {
    const itemsToSort = items || [];
    const sortedItems = [...itemsToSort];
    // .sort((a, b) => {
    //   return a.itemDescription
    //     .toLowerCase()
    //     .localeCompare(b.itemDescription.toLowerCase());
    // });
    return sortedItems;
};
